// ── Upload view ────────────────────────────────────────────────────────────────
// Renders the initial photo-upload screen with drag-and-drop support.
// The avatar is uploaded silently in the background as soon as an image is
// selected; "Try This On" is not blocked on that upload.

import { esc } from '../helpers';
import { longLogoHTML } from '../logos';
import { comboChooserHTML, comboCtaLabel, resolveTryonType, tryOnSubmitLabel, wireComboChooser } from '../combo-chooser';
import type { ViewContext } from '../modal-types';

export function renderUpload(ctx: ViewContext): void {
  const { state, content, set, openLogin, handleGuestOrAuthSubmit, selectFile, client } = ctx;

  // If the user is already a real logged-in user (not a guest), they arrived here
  // from the avatars view via "+ Upload New". Show a "Add New Photo" heading without
  // the Login link, which otherwise makes this look like a logged-out modal.
  const isLoggedIn = client.auth.isAuthenticated() && !client.auth.getCurrentUser()?.isGuest;

  const heading = isLoggedIn
    ? `<div class="ts-upload-heading"><strong>Add New Photo</strong></div>`
    : ``;

  const submitLabel = tryOnSubmitLabel(ctx.opts, state);
  const canCombo = !!resolveTryonType(ctx.opts, state);

  content.innerHTML = `
    ${ctx.headerHTML()}
    <div class="ts-mbody ts-flow-layout">
      <div class="ts-flow-panel">
      ${isLoggedIn ? `
        <div class="ts-topbar">
          <button class="ts-logout-btn" id="ts-logout" type="button" aria-label="Logout">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" class="ts-icon-svg">
              <path d="M20 12h-9.5m7.5 3l3-3-3-3m-5-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2h5a2 2 0 002-2v-1" 
                    stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.4"/>
            </svg>
          </button>
        </div>` : ''}
        ${state.error ? `<div class="ts-error">${esc(state.error)}</div>` : ''}
        ${state.preview ? '' : heading}
        ${state.preview ? `
        <div class="ts-up-preview">
          <div class="ts-dz ts-dz-preview" id="ts-dz" tabindex="0" role="button" aria-label="Change photo" title="Change photo">
            <img src="${state.preview}" alt="Preview" />
          </div>
          <div class="ts-btn-row">
            <button class="ts-btn-primary" id="ts-submit" type="button">${submitLabel}</button>
            <button class="ts-combo-cta" id="ts-combo-cta" type="button" ${canCombo ? '' : 'disabled'}>${comboCtaLabel(state)}</button>
          </div>
          ${comboChooserHTML(state)}
        </div>
        ` : `
        <div class="ts-dz" id="ts-dz">
          <div class="ts-up-copy">Upload a photo as guest, or create a free ${longLogoHTML(16)} account to use <strong>Privacy First</strong> and keep control of your media.</div>
          <div class="ts-up-actions">
            <button class="ts-btn-primary" id="ts-upload-btn" type="button">Upload Photo</button>
            ${isLoggedIn ? '' : `<button class="ts-combo-cta" id="ts-signin-btn" type="button">Sign in or Register</button>`}
          </div>
          <div class="ts-up-legal">Images are processed per ${longLogoHTML(12)} <a href="https://trystyle.fashion/privacypolicy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></div>
        </div>`}
        <input type="file" id="ts-file-inp" accept="image/png,image/jpeg,image/webp" style="display:none" />
        ${isLoggedIn ? `<button class="ts-btn-ghost" id="ts-back-avatars" type="button" style="margin-top:8px">← Back to My Avatars</button>` : ''}
      </div>
    </div>
  `;

  content.querySelector('#ts-logout')?.addEventListener('click', () => {
    void (async () => {
      if (state.preview) URL.revokeObjectURL(state.preview);
      await client.auth.logout();
      set({
        phase: 'upload',
        file: null,
        preview: null,
        progress: 0,
        resultUrl: null,
        resultImages: null,
        error: null,
        avatars: [],
        selectedAvatarId: null,
        sizeRec: null,
        avatarId: null,
        avatarStatus: 'idle',
        comboOpen: false,
        comboError: null,
        comboSections: [],
        selectedComboProduct: null,
        comboCategories: null,
        processingLabel: state.processingLabel,
      });
    })();
  });

  // Back button — only present for logged-in users
  content.querySelector('#ts-back-avatars')?.addEventListener('click', () => {
    set({ phase: 'avatars', file: null, preview: null, error: null, avatarId: null, avatarStatus: 'idle', comboOpen: false, comboError: null, comboSections: [], selectedComboProduct: null, comboCategories: null });
  });

  const dz      = content.querySelector<HTMLDivElement>('#ts-dz')!;
  const fileInp = content.querySelector<HTMLInputElement>('#ts-file-inp')!;

  // Photo selected through the file picker — shared with the avatars view's
  // "+ Upload New" (which opens the picker straight away). The avatar upload is
  // started silently in the background; "Try This On" awaits the same in-flight
  // request, so the user isn't blocked waiting for it.
  function acceptFile(f: File): void {
    selectFile(f);
  }

  // Empty state — actions are driven by the buttons; the box itself is not clickable.
  content.querySelector('#ts-upload-btn')?.addEventListener('click', () => fileInp.click());
  content.querySelector('#ts-signin-btn')?.addEventListener('click', openLogin);

  // Preview state — clicking the image lets the user pick a different photo.
  if (state.preview) {
    dz.addEventListener('click',     () => fileInp.click());
    dz.addEventListener('dragover',  e  => { e.preventDefault(); dz.classList.add('over'); });
    dz.addEventListener('dragleave', () => dz.classList.remove('over'));
    dz.addEventListener('drop', e => {
      e.preventDefault();
      dz.classList.remove('over');
      const f = e.dataTransfer?.files[0];
      if (f) acceptFile(f);
    });
  }
  fileInp.addEventListener('change', () => {
    if (fileInp.files?.[0]) acceptFile(fileInp.files[0]);
  });

  content.querySelector('#ts-submit')?.addEventListener('click', () => {
    void handleGuestOrAuthSubmit();
  });

  content.querySelector('#ts-combo-cta')?.addEventListener('click', () => {
    void ctx.onComboCta();
  });
  wireComboChooser(ctx);

  // Fade the selected photo in once it renders (no flash), and reflow the card
  // to hug the image with the edge padding.
  if (state.preview) {
    const previewImg = content.querySelector<HTMLImageElement>('.ts-dz.ts-dz-preview img');
    if (previewImg) {
      if (previewImg.complete && previewImg.naturalWidth > 0) {
        previewImg.classList.add('ts-img-loaded');
        ctx.reflow();
      } else {
        previewImg.addEventListener('load', () => {
          previewImg.classList.add('ts-img-loaded');
          ctx.reflow();
        });
      }
    }
  }
}
